import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, saveBlob } from "../../lib/api";
import { TvChart, TC, PALETA } from "../trading/TvChart";
import { TradingPanel } from "../trading/Panel";

export const P = (v, d = 1) => (v == null ? "—" : `${(v * 100).toFixed(d)}%`);
export const M = (v, cur = "MXN") => (v == null ? "—" : `${cur === "USD" ? "US$" : "$"}${Number(v).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`);
const pt = (arr) => (arr || []).map((v) => (v == null ? {} : { value: v }));
export const ln = (id, name, arr, color, type = "line", extra = {}) => ({ id, name, type, color, data: pt(arr), ...extra });
const FP = (v) => `${(v * 100).toFixed(1)}%`;
const FM = (v) => (Math.abs(v) >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : Math.abs(v) >= 1e3 ? `${(v / 1e3).toFixed(0)}k` : v.toFixed(0));
export const FMT = { pct: FP, money: FM, num: (v) => v.toFixed(0), x: (v) => `${v.toFixed(2)}x` };

export const Seg = ({ opts, v, set, testid }) => (
  <div className="flex flex-wrap rounded-lg border border-[var(--trd-border)] p-0.5" data-testid={testid}>{opts.map(([k, l]) => (
    <button key={k} type="button" onClick={() => set(k)} aria-pressed={v === k} data-testid={`${testid}-${k}`}
      className={`trd-mono rounded-md px-2 py-0.5 text-[11px] ${v === k ? "bg-[var(--trd-surface-2)] text-white" : "text-[var(--trd-muted)] hover:text-white"}`}>{l}</button>))}</div>
);

export const Vacio = ({ testid, txt = "Se llena con cada corte" }) => (
  <div className="flex h-40 items-center justify-center rounded-xl border border-dashed border-[var(--trd-border)] text-center text-xs text-[var(--trd-muted)]" data-testid={testid}>{txt}</div>
);

export const useExport = (params) => async (seccion) => {
  try {
    const r = await http.get("/desempeno/excel", { params: { ...params, seccion }, responseType: "blob" });
    saveBlob(r.data, `desempeno_${(seccion || "completo").replace(/ /g, "_")}.xlsx`);
  } catch (e) { toast.error(errMsg(e)); }
};

export const Xl = ({ onClick, testid }) => (
  <button type="button" onClick={onClick} className="trd-mono inline-flex items-center gap-1 rounded-md border border-[var(--trd-border)] px-2 py-0.5 text-[11px] text-[var(--trd-muted)] hover:text-white" data-testid={testid}><Download size={11} />Excel</button>
);

// Panel oscuro con gráfica de líneas; si hay menos meses que el mínimo, muestra el estado vacío honesto.
export const Linea = ({ title, testid, series, labels, fmt = FP, como, extra, ultimo, poco, priceLines }) => (
  <TradingPanel title={title} testid={testid} como={como} extra={extra} ultimo={ultimo}>
    {poco ? <Vacio testid={`${testid}-vacio`} /> : <TvChart series={series} labels={labels} fmt={fmt} height={240} toggle testid={`${testid}-chart`} priceLines={priceLines} />}
  </TradingPanel>
);

export const Apilado = ({ rows, keys, x, testid, fmt = FP, height = 260 }) => {
  const data = useMemo(() => rows, [rows]);
  return (
    <div style={{ height }} data-testid={testid}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={TC.grid} vertical={false} />
          <XAxis dataKey={x} tick={{ fill: TC.text, fontSize: 10 }} stroke={TC.border} />
          <YAxis tickFormatter={fmt} tick={{ fill: TC.text, fontSize: 10 }} stroke={TC.border} width={52} />
          <Tooltip cursor={{ fill: "#ffffff12" }} formatter={(v) => fmt(v)} contentStyle={{ background: "#111823", border: `1px solid ${TC.border}`, fontSize: 11 }} labelStyle={{ color: "#fff" }} />
          <Legend itemSorter={null} wrapperStyle={{ fontSize: 10, color: TC.text }} />
          {keys.map((k, i) => <Bar key={k} dataKey={k} stackId="a" fill={PALETA[i % PALETA.length]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const Tabla = ({ cols, rows, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="trd-mono w-full min-w-[480px] text-[11px]">
      <thead><tr className="text-left text-[var(--trd-muted)]">{cols.map((c) => <th key={c} className="px-2 py-1 font-semibold">{c}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[var(--trd-border)] text-[#C9D1D9]">{r.map((c, j) => <td key={j} className="whitespace-nowrap px-2 py-1">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

export const Cambios = ({ c, testid }) => (
  <dl className="grid grid-cols-3 gap-2 text-center" data-testid={testid}>{[["MoM", c?.mom], ["QoQ", c?.qoq], ["YoY", c?.yoy]].map(([k, v]) => (
    <div key={k} className="rounded-lg bg-[var(--trd-surface-2)] p-2"><dt className="text-[10px] text-[var(--trd-muted)]">{k}</dt>
      <dd className={`trd-mono text-sm ${v == null ? "text-[var(--trd-muted)]" : v >= 0 ? "trd-good" : "trd-bad"}`} data-testid={`${testid}-${k.toLowerCase()}`}>{v == null ? "Sin historia" : `${v >= 0 ? "▲" : "▼"} ${(Math.abs(v) * 100).toFixed(1)}%`}</dd></div>))}</dl>
);

export const useSel = (def) => useState(def);
